import { getMixpanelDistinctId, globalMixpanelData, identify, track } from "@/utils/mixpanel";
import { createBranchLink } from "@/api";
import { AppPlatform } from "@/constants";
import Mixpanel from "mixpanel-browser";
import { MixpanelEvents } from "@/constants/mixpanel.js";
import { updateCustomer } from "@/features/stripe-precheckout-payment-elements/StripePaymentContainer";
import { initPixel } from "@/utils/pixel";

export const trackEvent = async ({ eventName, authToken, data }) => {
    if (!eventName) {
        throw new Error("Event name is required");
    }

    try {
        const path = authToken ? "/events" : "/autls/events";
        const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}${path}`, {
            method: "POST",
            body: JSON.stringify({
                event_name: eventName,
                distinct_id: getMixpanelDistinctId(),
                data: {
                    email: data.email,
                    branch_link: data.branchLink,
                    distinct_id: data.distinctId,
                }
            }),
            headers: {
                app_version: process.env.NEXT_PUBLIC_APP_VERSION,
                app_platform: AppPlatform.TT,
                ...(authToken && { Authorization: `Bearer ${authToken}` }),
            }
        });

        const result = await response.json();
        return result;
    } catch (error) {
        console.error("Error tracking event", error);
        track("track_event_api_error", {
            eventName,
            data,
            error: error.message,
        });
        return null;
    }
};

export const signup = async ({ email, clientType }) => {
    const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/signup`, {
        method: "POST",
        body: JSON.stringify({
            email,
            password: crypto.randomUUID(),
            distinct_id: getMixpanelDistinctId(),
            client_type: clientType,
        }),
        headers: {
            app_version: process.env.NEXT_PUBLIC_APP_VERSION,
            app_platform: clientType,
        },
    });

    return response.json();
};

const TrackableEvents = {
    TOP_TEN_AUTH_SESSION: "TOP_TEN_START_SESSION",
    TOP_TEN_AUTHLESS_SESSION: "TOP_TEN_FEEDBACK",
    WELCOME_EMAIL_SEND: "WELCOME_EMAIL_SEND",
}

const trackSendEmail = ({ data, authToken }) => {
    trackEvent({
        eventName: TrackableEvents.WELCOME_EMAIL_SEND,
        authToken,
        data,
    });
};

export const trackTopTenStartSession = (props) => {
    Mixpanel.track(MixpanelEvents.TT_START_SESSION, props);
    initPixel()
        .then(ReactPixel => {
            console.log('pixel track top ten start session', ReactPixel);
            ReactPixel.default.trackCustom(MixpanelEvents.TT_START_SESSION, props);
        })
};


export const updateRevenueCat = async ({ userId, subscriptionId }) => {
    return await fetch("https://api.revenuecat.com/v1/receipts", {
        method: 'POST',
        headers: {
            "X-Platform": "stripe",
            "Content-Type": "application/json",
            "Authorization": `Bearer ${process.env.NEXT_PUBLIC_REVENUECAT_PUBLIC_STRIPE_API_KEY}`,
        },
        body: JSON.stringify({
            app_user_id: userId,
            fetch_token: subscriptionId,
        }),
    });
}

export const cancelSubscription = async (subscriptionId) => {
    return await fetch("/quiz/stripe/subscription-cancel", {
        method: 'POST',
        body: JSON.stringify({ subscriptionId }),
    });
}

export const getAutoLoginLink = async ({
    email,
    clientType,
    pageId,
    link,
    referringLinkUrl,
    branchData,
    subscriptionId,
    cancelSubscriptionId,
    sendWelcomeEmail = false,
    authToken: initialAuthToken,
    userId: initialUserId,
    customerId,
}) => {
    let authToken;
    let userId;

    if (customerId && email) {
        updateCustomer({ type: "third_party", customerId, email });
    }

    try {
        if (!initialAuthToken || !initialUserId) {
            const data = await signup({ email, clientType });
            userId = data.user?._id;
            authToken = data.accessToken;
        }

        if (!userId || !authToken) {
            throw new Error("User ID or authorization token is missing");
        }

        Mixpanel.identify(userId);

        if (userId && authToken) {
            trackEvent({
                eventName: TrackableEvents.TOP_TEN_AUTH_SESSION,
                authToken,
                data: {
                    ...globalMixpanelData,
                    distinctId: getMixpanelDistinctId(),
                    userId,
                },
            });
        } else {
            trackEvent({
                eventName: TrackableEvents.TOP_TEN_AUTHLESS_SESSION,
                data: globalMixpanelData,
            });
        };

        await Promise.all([
            subscriptionId && updateRevenueCat({ userId, subscriptionId }),
            cancelSubscriptionId && cancelSubscription(cancelSubscriptionId)
        ].filter(Boolean));

        Mixpanel.track("web2app-checkout-success-stripe-success", {
            subscription: subscriptionId,
            user_id: userId,
            source: "",
        });

        const branchLink = await createBranchLink({
            id: pageId,
            isAuth: true,
            authToken,
            reason: 'landing_page',
            userId,
            referringLinkUrl,
            branchData: {
                "~stage": "signup",
                ...branchData
            },
        });

        if (sendWelcomeEmail) {
            trackSendEmail({
                authToken,
                data: {
                    ...globalMixpanelData,
                    email,
                    distinctId: getMixpanelDistinctId(),
                    branchLink,
                    userId,
                },
            });
        }

        const finalLink = branchLink || link;

        return {
            userId,
            authToken,
            branchLink: finalLink,
        };
    } catch (error) {
        track("web2app-checkout-success-stripe-error", {
            subscription: subscriptionId,
            user_id: userId,
            error: error.message,
        });
        throw error;
    }
};

export const getCheckoutSessionUrl = async ({
    email,
    link,
    pageId,
    trialVariation,
    priceTestId,
    priceTest,
    priceTestVariation,
    couponId,
    allowPromotionCode = false,
    anonymous = false,
    clientType = "",
}) => {
    const { userId, branchLink } = await getAutoLoginLink({
        email,
        clientType,
        pageId,
        link,
    });

    const testNoEmail = new URLSearchParams(window.location.search).get('testNoEmail') || anonymous;
    const payPerAcquisitionHash = new URLSearchParams(window.location.search).get('ppa');
    const costPerClick = new URLSearchParams(window.location.search).get('tp');

    // this is using the nextjs api route
    const checkoutResponse = await fetch("/best/checkout", {
        method: "POST",
        credentials: "include", // Important: This allows cookies to be sent
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            email,
            userId,
            noEmail: testNoEmail,
            link: branchLink,
            isQuizFlow: true,
            sourceUrl: window.location.href,
            ...(trialVariation && { withTrial: trialVariation }),
            ...(priceTest && { priceTest }),
            priceTestVariation,
            designVersion: "",
            flowName: "",
            payPerAcquisitionHash,
            costPerClick,
            priceTestId,
            couponId,
            allowPromotionCode,
        }),
    });
    const checkoutResults = await checkoutResponse.json();

    if (checkoutResults.sessionUrl) {
        return {
            sessionUrl: checkoutResults.sessionUrl,
            sessionLink: checkoutResults.sessionUrl, // for backward compatibility
            branchLink,
            userId
        };
    }

    return { userId };
};
